import { Search, ViewColumnOutlined } from '@mui/icons-material';
import {
  Checkbox,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';

import { COLUMNS, GroupBy, GROUPS } from '../config';
import { IssueListSettings } from '../useIssueListSettings';

import s from './IssueListControls.module.scss';

type Props = {
  query: string;
  onQueryChange: (query: string) => void;
  /** «найдено / всего» справа в поле поиска */
  counter: string;
  settings: IssueListSettings;
  onGroupByChange: (groupBy: GroupBy) => void;
  onToggleColumn: (column: IssueListSettings['columns'][number]) => void;
  /** Фильтры экрана — между поиском и настройками */
  children?: React.ReactNode;
};

/** Панель над списком задач: поиск, фильтры экрана, группировка, колонки */
const IssueListControls: React.FC<Props> = ({
  query,
  onQueryChange,
  counter,
  settings,
  onGroupByChange,
  onToggleColumn,
  children,
}) => {
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLElement | null>(null);

  return (
    <div className={s.controls}>
      <TextField
        size="small"
        sx={{ width: 320 }}
        placeholder="Поиск по названию или ключу"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        slotProps={{
          htmlInput: { 'aria-label': 'Поиск задач' },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: <span className={s.counter}>{counter}</span>,
          },
        }}
      />

      {children}

      <div className={s.controls__end}>
        <TextField
          select
          size="small"
          sx={{ width: 220 }}
          value={settings.groupBy}
          onChange={(event) => onGroupByChange(event.target.value as GroupBy)}
          slotProps={{
            select: {
              // Подпись в самом значении, как в фильтрах Яндекс Трекера, — без «плавающего» label
              renderValue: (value) =>
                `Группировка: ${value === 'none' ? 'нет' : GROUPS[value as Exclude<GroupBy, 'none'>].label}`,
              SelectDisplayProps: { 'aria-label': 'Группировка' },
            },
          }}
        >
          <MenuItem value="none">Без группировки</MenuItem>
          {Object.entries(GROUPS).map(([id, group]) => (
            <MenuItem key={id} value={id}>
              {group.label}
            </MenuItem>
          ))}
        </TextField>

        <Tooltip title="Колонки">
          <IconButton
            aria-label="Настроить колонки"
            onClick={(event) => setColumnsAnchor(event.currentTarget)}
          >
            <ViewColumnOutlined />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={columnsAnchor}
          open={!!columnsAnchor}
          onClose={() => setColumnsAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {COLUMNS.filter((column) => !column.isRequired).map((column) => (
            <MenuItem key={column.id} dense onClick={() => onToggleColumn(column.id)}>
              <Checkbox
                size="small"
                edge="start"
                checked={settings.columns.includes(column.id)}
                tabIndex={-1}
                disableRipple
              />
              <ListItemText>{column.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </div>
    </div>
  );
};

export { IssueListControls };
